import LegalLayout from "../components/LegalLayout";

const SECTION_TITLE = "font-serif text-2xl text-[#2C2A29] mt-10 mb-3";
const PARA = "text-[15px] leading-[1.7] text-[#3F3D3B]";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="February 2026">
      <p className={PARA}>
        Guardian Grace Family &amp; Community Foundation (&ldquo;Guardian Grace,&rdquo;
        &ldquo;we,&rdquo; &ldquo;our&rdquo;) is a registered 501(c)(3) nonprofit
        organization (EIN&nbsp;41-2776772). We respect your privacy. This policy
        explains what information we collect when you use our website, how we use it,
        and the choices you have.
      </p>

      <h2 className={SECTION_TITLE}>1. Information We Collect</h2>
      <p className={PARA}>
        <strong>Information you provide directly.</strong> When you subscribe to our
        newsletter, send us a message through the contact form, or make a donation, we
        collect the information you enter — such as your name, email address, optional
        subject and message, and (for donors) the amount you choose to give.
      </p>
      <p className={PARA}>
        <strong>Payment information.</strong> Donations are processed by Stripe, Inc.
        Card details are entered directly into Stripe&rsquo;s secure checkout and are
        never stored on our servers. We only retain the transaction ID, donation amount,
        currency, status, and the donor name/email you choose to share with us.
      </p>
      <p className={PARA}>
        <strong>Automatically collected.</strong> Like most websites, we collect basic
        technical data such as your IP address, browser type, device, and pages visited.
        We use this anonymized data to improve the site experience and understand how
        people find our work.
      </p>

      <h2 className={SECTION_TITLE}>2. How We Use Your Information</h2>
      <ul className="list-disc pl-6 space-y-2 text-[15px] leading-[1.7] text-[#3F3D3B]">
        <li>To respond to your inquiry or process your donation.</li>
        <li>To send you newsletter updates and stories of impact, if you opted in.</li>
        <li>To send donation receipts and acknowledgments.</li>
        <li>To comply with legal, tax, and accounting obligations.</li>
        <li>To improve our website and the programs we offer.</li>
      </ul>

      <h2 className={SECTION_TITLE}>3. Sharing &amp; Disclosure</h2>
      <p className={PARA}>
        We do <strong>not</strong> sell, rent, or trade your personal information. We
        share data only with the trusted service providers we use to operate the site
        and our mission:
      </p>
      <ul className="list-disc pl-6 space-y-2 text-[15px] leading-[1.7] text-[#3F3D3B]">
        <li><strong>Stripe</strong> — donation processing (see Stripe&rsquo;s privacy policy).</li>
        <li><strong>Resend</strong> — sending transactional emails (receipts, auto-replies, newsletter notifications).</li>
        <li><strong>MongoDB Atlas</strong> — secure database hosting for subscribers, messages, and donation records.</li>
      </ul>
      <p className={PARA}>
        We may disclose information if required by law, subpoena, or to protect our
        rights, the safety of our community, or the integrity of our operations.
      </p>

      <h2 className={SECTION_TITLE}>4. Your Choices &amp; Rights</h2>
      <ul className="list-disc pl-6 space-y-2 text-[15px] leading-[1.7] text-[#3F3D3B]">
        <li><strong>Unsubscribe</strong> from our newsletter anytime by replying with &ldquo;unsubscribe&rdquo; or contacting us directly.</li>
        <li><strong>Request a copy</strong> of the information we hold about you.</li>
        <li><strong>Request deletion</strong> of your information, subject to legal record-keeping requirements (e.g., donation records for tax purposes).</li>
        <li><strong>Correct</strong> inaccurate information.</li>
      </ul>
      <p className={PARA}>
        To exercise any of these rights, email{" "}
        <a href="mailto:info@guardiangrace.org" className="text-[#692128] underline-offset-4 hover:underline">
          info@guardiangrace.org
        </a>
        .
      </p>

      <h2 className={SECTION_TITLE}>5. Data Retention</h2>
      <p className={PARA}>
        We retain personal information only as long as needed to fulfill the purpose
        for which it was collected. Donation records may be retained for at least
        seven (7) years to comply with IRS and state requirements for charitable
        organizations.
      </p>

      <h2 className={SECTION_TITLE}>6. Cookies &amp; Analytics</h2>
      <p className={PARA}>
        We use minimal, privacy-respecting analytics to understand site usage. We do
        not use cookies to track you across other websites or for advertising purposes.
      </p>

      <h2 className={SECTION_TITLE}>7. Children&rsquo;s Privacy</h2>
      <p className={PARA}>
        Our site is not directed to children under 13, and we do not knowingly collect
        information from them. If you believe a child has provided personal information
        through our site, please contact us so we can remove it.
      </p>

      <h2 className={SECTION_TITLE}>8. Security</h2>
      <p className={PARA}>
        We take reasonable technical and organizational measures to protect your data,
        including encrypted transport (HTTPS), industry-standard payment processing,
        and access controls. No method of transmission over the internet is 100% secure,
        but we work hard to safeguard your information.
      </p>

      <h2 className={SECTION_TITLE}>9. Updates to This Policy</h2>
      <p className={PARA}>
        We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo;
        date above will always reflect the latest revision. Material changes will be
        communicated to subscribers via email.
      </p>

      <h2 className={SECTION_TITLE}>10. Contact</h2>
      <p className={PARA}>
        Questions about this policy? Reach out:
        <br />
        Guardian Grace Family &amp; Community Foundation
        <br />
        1820 Highway 20 SE, Suite 114
        <br />
        Conyers, GA 30013
        <br />
        <a href="mailto:info@guardiangrace.org" className="text-[#692128] underline-offset-4 hover:underline">
          info@guardiangrace.org
        </a>
      </p>

      <p className="mt-12 text-xs text-[#5C5856] italic">
        This Privacy Policy is provided as a starting template. Please have it reviewed by qualified legal counsel before relying on it in production.
      </p>
    </LegalLayout>
  );
}
